import { useEffect, useMemo, useRef, useState } from "react"

import { App, Button, Flex, Form, Grid, Layout, Space, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import ReactPlayer from "react-player"
import { connect, useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import BillingDescriptorsForm from "../../../Components/Forms/BillingDescriptorsForm"
import Icon from "../../../Icon"
import { modalConfig } from "../../../theme"
import { subStepsStorage } from "../../../Utils/onboarding/setStatusSubSteps"
import { createAccountStepPath, onboardingStepPath } from "../../../Utils/utils"
import { BA_STEPS } from "../../GettingStartedPage/utils/businessSteps"
import { OB_STEPS } from "../../GettingStartedPage/utils/onboardingSteps"
import UserActions from "../../User/reducer"
import { getDescriptorsArnCodesSuggestions } from "../../User/services"
import MobileCtaButtons from "../PaymentConnectPage/components/MobileCtaButtons"
import BillingDescriptorArticleCard from "./components/BillingDescriptorArticleCard"
import { getVideoUrl, VIDEO_MAP, VIDEO_TITLES } from "./utils/constants/videoMap"
import { getDescription } from "./utils/helpers/getDescription"
import { getTitle } from "./utils/helpers/getTitle"

import "./styles.less"

const { Title, Text } = Typography
const { useBreakpoint } = Grid

const OnboardingAlertsPage = (props) => {
  const {
    auth: { error, fetching, userInfo, isAdmin },
    nav: { isMobile, theme },
    userFetching,
    businessAccount,
    paymentProcessors,
    enrollments,
    nextCreateAccountStep,
    isManualRefundsConnected,
    billingDescriptors,
    errorDescriptors,
    errorBinCodes,
    errorCaidCodes,
    step,
    maxStep
  } = props

  const dispatch = useDispatch()
  const { enrollmentId } = useParams()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { modal, notification } = App.useApp()
  const validateBillingDescriptorResult = useSelector((s) => s.user.validateBillingDescriptorResult)
  const [subStep, setSubStep] = useState(() => {
    const savedSubStep = subStepsStorage.get()
    return savedSubStep === "bin" || savedSubStep === "arn" ? 2 : 1
  })

  const { sm, lg } = useBreakpoint()

  const [videoUrl, setVideoUrl] = useState("")
  const [videoFailed, setVideoFailed] = useState(false)
  const [suggestions, setSuggestions] = useState(null)
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  const [suggestionsSettled, setSuggestionsSettled] = useState(false)
  const suggestionsRequested = useRef(false)
  const hydratedRef = useRef(false)
  const initialBillingDescriptorsRef = useRef(billingDescriptors)

  const isUseBinCaidCodes = Form.useWatch("use_bin_caid_codes", form)

  const isNewBusiness = pathname?.includes("/create-account")
  const currentBusinessAccountId = userInfo?.["current_business_account_id"]
  const businessId =
    businessAccount?.id === currentBusinessAccountId
      ? businessAccount?.id
      : currentBusinessAccountId
  const selectedPayment = paymentProcessors
    ?.filter((pp) => pp?.["is_connected"])
    ?.map((pp) => pp?.["service_name"])
  const videoTitle = VIDEO_TITLES[selectedPayment?.[0]] || VIDEO_TITLES.default
  const showArticle = selectedPayment?.length > 1 || !VIDEO_MAP?.[selectedPayment?.[0]]

  const activeEnrollments = enrollments?.filter((enrollment) => enrollment.is_active)
  const activeEnrollmentsId = enrollments
    ?.filter((enrollment) => enrollment.is_active)
    ?.map((el) => el.id)

  const activeEnrollmentsHash = enrollmentId || activeEnrollmentsId?.join(",")
  const isActiveRDR = enrollmentId
    ? enrollments.find((el) => el.id === enrollmentId)?.name === "RDR"
    : activeEnrollments.some((enrollment) => enrollment.name === "RDR")
  const isCreatingBa = !userInfo?.new_onboarding_finished || nextCreateAccountStep
  const estimatedDescriptors = useMemo(
    () =>
      suggestions?.merchant_descriptors
        ?.filter((descriptor) => descriptor.is_estimated)
        ?.map((descriptor) => descriptor.value) || [],
    [suggestions]
  )
  const initialValues = useMemo(() => {
    const billingDescriptorsNotEmpty = billingDescriptors?.descriptor_data?.filter(
      (el) => el?.["merchant_descriptor"]
    )
    const billingDescriptorsList = billingDescriptorsNotEmpty?.map(
      (el) => el["merchant_descriptor"]
    )
    const temporaryDescriptorsList = billingDescriptorsNotEmpty
      ?.filter((el) => el?.["is_temporary"])
      ?.map((el) => el["merchant_descriptor"])
    const allVisaArnCodesList =
      billingDescriptorsNotEmpty?.flatMap((el) => el["visa_arn_codes"]) || []
    const visaArnCodesList = [...new Set(allVisaArnCodesList)]
    const allVisaBinCodesList =
      billingDescriptorsNotEmpty?.flatMap((el) => el["visa_bin_codes"]) || []
    const visaBinCodesList = [...new Set(allVisaBinCodesList)]
    const allVisaCaidCodesList =
      billingDescriptorsNotEmpty?.flatMap((el) => el["visa_caid_codes"]) || []
    const visaCaidCodesList = [...new Set(allVisaCaidCodesList)]

    return {
      merchant_descriptor: billingDescriptorsList || [],
      temporary_descriptor: temporaryDescriptorsList || [],
      visa_arn_codes: visaArnCodesList || [],
      visa_bin_codes: visaBinCodesList || [],
      visa_caid_codes: visaCaidCodesList || [],
      use_bin_caid_codes: billingDescriptors?.use_bin_caid_codes || false
    }
  }, [billingDescriptors])

  useEffect(() => {
    const savedSubStep = subStepsStorage.get()

    if (savedSubStep === "arn" && isActiveRDR) {
      form.setFieldValue("use_bin_caid_codes", true)
    }

    if (!isActiveRDR) {
      subStepsStorage.set("billing")
      setSubStep(1)
    }
  }, [])

  useEffect(() => {
    if (selectedPayment.length === 1) {
      setVideoUrl(getVideoUrl(selectedPayment[0], subStep === 2 ? "bin" : "billing"))
      return
    }

    if (selectedPayment.length > 1) {
      setVideoUrl(VIDEO_MAP.default)
      return
    }

    setVideoUrl("")
  }, [selectedPayment, subStep])

  useEffect(() => {
    setVideoFailed(false)
  }, [videoUrl])

  useEffect(() => {
    if (!validateBillingDescriptorResult) return
    dispatch(UserActions.validateBillingDescriptorReset())
    setSubStep(2)
  }, [validateBillingDescriptorResult])

  useEffect(() => {
    if (userInfo && businessId) {
      dispatch(
        UserActions.getPaymentProcessorsRequest({
          merchant_id: businessId,
          return_url: process.env.REACT_APP_BASE_URI + location.pathname
        })
      )
    }
    document.title = "Payment processor connect - Chargeback"
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [fetching, businessId])

  // keep enrollments in sync with the account the descriptors are submitted for
  useEffect(() => {
    if (userInfo && businessId) {
      dispatch(UserActions.getEnrollmentsRequest(businessId))
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [businessId])

  const openModalConfirm = () => {
    modal.confirm({
      ...modalConfig("info", 442),
      className: "ob-clear-bg cdrn",
      centered: !isMobile,

      title: t("We are missing some information..."),
      content: (
        <Text type="secondary">
          {t(
            "Warning, you have not provided us with enough information to turn on chargeback prevention for your account."
          )}
        </Text>
      ),
      cancelText: t("Cancel"),
      okText: t("Continue anyway"),
      cancelButtonProps: { size: "large" },
      okButtonProps: { size: "large" },
      onOk() {
        handleSubmit({ not_validate: true })
        if (isNewBusiness) subStepsStorage.remove()
      },
      onCancel() {}
    })
  }

  const onSubmit = (data) => {
    handleSubmit(data)
  }

  const handleSubmit = async ({ not_validate }) => {
    try {
      const merchant_descriptor = form.getFieldValue("merchant_descriptor") ?? []
      const visa_arn_codes = form.getFieldValue("visa_arn_codes") ?? []
      const visa_bin_codes = form.getFieldValue("visa_bin_codes") ?? []
      const visa_caid_codes = form.getFieldValue("visa_caid_codes") ?? []
      const use_bin_caid_codes = form.getFieldValue("use_bin_caid_codes")

      await form.validateFields([
        ...(not_validate ? [] : ["merchant_descriptor"]),
        ...(not_validate ? [] : ["visa_arn_codes"]),
        "use_bin_caid_codes",
        ...(not_validate ? [] : ["visa_bin_codes"]),
        ...(not_validate ? [] : ["visa_caid_codes"])
      ])

      const visaArnCodesArray = Array.isArray(billingDescriptors?.visa_arn_codes)
        ? billingDescriptors.visa_arn_codes
        : []

      let deleted_visa_arn_codes = visaArnCodesArray.filter(
        (code) => !visa_arn_codes?.includes(code?.["arn_code"])
      )
      if (enrollmentId) {
        if (
          (initialValues?.["merchant_descriptor"].join("") === [...merchant_descriptor].join("") &&
            initialValues?.["visa_arn_codes"].join("") === [...visa_arn_codes].join("") &&
            !use_bin_caid_codes) ||
          (initialValues?.["merchant_descriptor"].join("") === [...merchant_descriptor].join("") &&
            initialValues?.["visa_bin_codes"].join("") === [...visa_bin_codes].join("") &&
            initialValues?.["visa_caid_codes"].join("") === [...visa_caid_codes].join("") &&
            use_bin_caid_codes)
        ) {
          let enrollmentPage = pathname.split("/").slice(0, -2).join("/")
          props.clearStep()
          navigate(enrollmentPage, { replace: true })
        } else {
          let finalDescriptorList = merchant_descriptor.map((el) => ({
            account_id: businessId,
            merchant_descriptor: el,
            enrollments: [enrollmentId],
            visa_arn_codes,
            ...(billingDescriptors?.visa_arn_codes?.length &&
              deleted_visa_arn_codes?.length && { deleted_visa_arn_codes }),
            visa_bin_codes: use_bin_caid_codes ? visa_bin_codes : [],
            visa_caid_codes: use_bin_caid_codes ? visa_caid_codes : [],
            use_bin_caid_codes
          }))

          if (!finalDescriptorList.length) {
            finalDescriptorList = isAdmin
              ? []
              : [
                  {
                    account_id: businessId,
                    merchant_descriptor: null,
                    enrollments: [enrollmentId],
                    visa_arn_codes,
                    ...(billingDescriptors?.visa_arn_codes?.length &&
                      deleted_visa_arn_codes?.length && {
                        deleted_visa_arn_codes
                      }),
                    visa_bin_codes: use_bin_caid_codes ? visa_bin_codes : [],
                    visa_caid_codes: use_bin_caid_codes ? visa_caid_codes : [],
                    use_bin_caid_codes
                  }
                ]
          }

          let deleteDescriptorList = [...initialValues?.["merchant_descriptor"]]
            ?.filter((el) => !merchant_descriptor?.includes(el))
            .map((el) => ({
              account_id: businessId,
              merchant_descriptor: el,
              enrollments: [enrollmentId],
              is_delete: true,
              ...(billingDescriptors?.visa_arn_codes?.length &&
                deleted_visa_arn_codes?.length && { deleted_visa_arn_codes })
            }))

          const data = {
            forEnrollment: true,
            isAdmin,
            ...(isAdmin && { ba_id: businessId }),
            enrollmentId,
            descriptorList: [...finalDescriptorList, ...deleteDescriptorList]
          }
          if (subStep === 1 && isActiveRDR && !isUseBinCaidCodes) {
            dispatch(UserActions.validateBillingDescriptorRequest(data))
            subStepsStorage.set("bin")
            return
          }
          props.createBillingDescriptor(data)
        }
      } else {
        let finalDescriptorList = merchant_descriptor.map((el) => ({
          account_id: businessId,
          merchant_descriptor: el,
          enrollments: activeEnrollmentsId,
          visa_arn_codes,
          ...(billingDescriptors?.visa_arn_codes?.length &&
            deleted_visa_arn_codes?.length && { deleted_visa_arn_codes }),
          visa_bin_codes: use_bin_caid_codes ? visa_bin_codes : [],
          visa_caid_codes: use_bin_caid_codes ? visa_caid_codes : []
        }))

        const data = {
          dont_remember_descriptors: merchant_descriptor?.length ? false : true,
          dont_remember_arn_codes: !visa_arn_codes?.length ? true : false,
          use_bin_caid_codes,
          dont_remember_bin_codes: !visa_bin_codes?.length ? true : false,
          dont_remember_caid_codes: !visa_caid_codes?.length ? true : false,
          descriptor_data: !finalDescriptorList?.length
            ? [
                {
                  account_id: businessId,
                  merchant_descriptor: null,
                  enrollments: activeEnrollmentsId,
                  visa_arn_codes,
                  ...(billingDescriptors?.visa_arn_codes?.length &&
                    deleted_visa_arn_codes?.length && {
                      deleted_visa_arn_codes
                    }),
                  visa_bin_codes: use_bin_caid_codes ? visa_bin_codes : [],
                  visa_caid_codes: use_bin_caid_codes ? visa_caid_codes : []
                }
              ]
            : [...finalDescriptorList]
        }
        if (subStep === 1 && isActiveRDR && !isUseBinCaidCodes) {
          dispatch(UserActions.validateBillingDescriptorRequest(data))
          subStepsStorage.set("bin")
          return
        }
        props.createBillingDescriptor(data)
      }
    } catch (errorInfo) {
      if (isCreatingBa) {
        const visaArnErrors = errorInfo.errorFields?.filter(
          (errorField) => errorField.name?.[0] === "visa_arn_codes"
        )

        const visaBinErrors = errorInfo.errorFields?.filter(
          (errorField) => errorField.name?.[0] === "visa_bin_codes"
        )

        const visaCaidErrors = errorInfo.errorFields?.filter(
          (errorField) => errorField.name?.[0] === "visa_caid_codes"
        )

        const hasVisaArnErrors =
          visaArnErrors?.length > 0 && visaArnErrors.some((error) => error.errors?.length > 0)

        const hasVisaBinErrors =
          visaBinErrors?.length > 0 && visaBinErrors.some((error) => error.errors?.length > 0)

        const hasVisaCaidErrors =
          visaCaidErrors?.length > 0 && visaCaidErrors.some((error) => error.errors?.length > 0)

        const use_bin_caid_codes = form.getFieldValue("use_bin_caid_codes")
        const visa_bin_codes = form.getFieldValue("visa_bin_codes")
        const visa_caid_codes = form.getFieldValue("visa_caid_codes")
        const visa_arn_codes = form.getFieldValue("visa_arn_codes")

        if (
          !use_bin_caid_codes &&
          (!hasVisaArnErrors || (hasVisaArnErrors && !visa_arn_codes?.length))
        ) {
          openModalConfirm()
        } else if (
          use_bin_caid_codes &&
          (!hasVisaBinErrors || (hasVisaBinErrors && !visa_bin_codes?.length)) &&
          (!hasVisaCaidErrors || (hasVisaCaidErrors && !visa_caid_codes?.length))
        ) {
          openModalConfirm()
        } else {
          console.log("Visa bin codes errors:", visaBinErrors)
          console.log("Visa caid codes errors:", visaCaidErrors)
          console.log("Modal not opened due to validation errors")
        }
      }
    }
  }

  useEffect(() => {
    document.title = "Alerts - Chargeback"
  }, [])

  useEffect(() => {
    if (activeEnrollmentsHash) props.getBillingDescriptors({ enrollments: activeEnrollmentsHash })
  }, [activeEnrollmentsHash])

  // Hydrate the form exactly once, after both the saved descriptors and the
  // suggestions have settled. Keeping it one-shot means later billingDescriptors
  // refetches (e.g. after a save) no longer resurrect values the user deleted.
  useEffect(() => {
    if (hydratedRef.current) return

    const billingDescriptorsSettled =
      !activeEnrollmentsHash || billingDescriptors !== initialBillingDescriptorsRef.current
    const suggestionsSettledGate = isAdmin || suggestionsSettled

    if (!billingDescriptorsSettled || !suggestionsSettledGate) return

    const useBinCaidNow = form.getFieldValue("use_bin_caid_codes")
    const hasDescriptorData = billingDescriptors?.descriptor_data?.length

    const descriptorSuggestions =
      suggestions?.merchant_descriptors?.map((descriptor) => descriptor.value) || []
    const merchant_descriptor = [
      ...new Set([...(initialValues.merchant_descriptor || []), ...descriptorSuggestions])
    ]

    // Preserve today's ARN precedence: the descriptor_data (string) form wins
    // when present, otherwise the top-level object form is mapped.
    let visa_arn_codes = hasDescriptorData
      ? initialValues.visa_arn_codes || []
      : billingDescriptors?.visa_arn_codes?.map((el) => el["arn_code"]) ||
        initialValues.visa_arn_codes ||
        []

    if (isActiveRDR && !useBinCaidNow) {
      const arnSuggestions =
        suggestions?.visa_arn_codes?.filter((arn) => arn.collectible)?.map((arn) => arn.code) || []
      visa_arn_codes = [...new Set([...visa_arn_codes, ...arnSuggestions])]
    }

    const visa_bin_codes = hasDescriptorData
      ? initialValues.visa_bin_codes || []
      : billingDescriptors?.visa_bin_codes?.map((el) => el["bin_code"]) ||
        initialValues.visa_bin_codes ||
        []
    const visa_caid_codes = hasDescriptorData
      ? initialValues.visa_caid_codes || []
      : billingDescriptors?.visa_caid_codes?.map((el) => el["caid_code"]) ||
        initialValues.visa_caid_codes ||
        []

    const use_bin_caid_codes =
      useBinCaidNow ||
      Boolean(billingDescriptors?.use_bin_caid_codes) ||
      initialValues.use_bin_caid_codes ||
      false

    form.setFieldsValue({
      ...initialValues,
      merchant_descriptor,
      visa_arn_codes,
      visa_bin_codes,
      visa_caid_codes,
      use_bin_caid_codes
    })

    hydratedRef.current = true
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [
    billingDescriptors,
    suggestions,
    suggestionsSettled,
    isAdmin,
    isActiveRDR,
    initialValues,
    activeEnrollmentsHash
  ])

  // The endpoint is a cheap no-op when no integration is connected, so we
  // don't gate on paymentProcessors (not loaded on the configure route).
  useEffect(() => {
    if (isAdmin) {
      setSuggestionsSettled(true)
      return
    }

    if (suggestionsRequested.current) return

    suggestionsRequested.current = true
    setSuggestionsLoading(true)

    getDescriptorsArnCodesSuggestions()
      .then((res) => {
        if (!res?.ok || !res?.data) return

        setSuggestions(res.data)

        res.data.integrations
          ?.filter((integration) => integration.error)
          ?.forEach(({ provider, error }) => {
            notification.warning({
              message: provider === "shopify" ? "Shopify" : "Stripe",
              description:
                error === "token_needs_reconnect"
                  ? t("Reconnect the integration to get suggestions.")
                  : t("Suggestions are unavailable right now. You can enter values manually.")
            })
          })
      })
      .finally(() => {
        setSuggestionsLoading(false)
        setSuggestionsSettled(true)
      })
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [isAdmin])

  const handleBack = () => {
    if (subStep === 1) {
      if (isNewBusiness) {
        navigate(createAccountStepPath[BA_STEPS.ENROLLMENT])
        return
      }
      navigate(onboardingStepPath[OB_STEPS.ENROLLMENT])
      return
    }
    setSubStep(1)
    subStepsStorage.set("billing")
  }

  const handleNavigate = () => {
    if (isNewBusiness) {
      navigate(createAccountStepPath[7])
      return
    }
    navigate(onboardingStepPath[2])
  }

  return (
    <Layout>
      <Spin size="large" spinning={fetching}>
        <div className="ob-alerts-step-content" style={{ maxWidth: 656 }}>
          {!isMobile && (
            <Button
              className="back-btn"
              type="text"
              icon={<Icon type="arrow_left" />}
              style={{ margin: " 0 0 12px 0px" }}
              onClick={handleBack}
            >
              Back
            </Button>
          )}
          <Flex
            vertical
            align={"center"}
            style={{
              width: "100%"
              // maxWidth: 1040,
            }}
          >
            <Flex
              gap={16}
              justify="space-between"
              align="center"
              style={{ width: "100%" }}
              wrap="wrap"
            >
              <Space direction="vertical" size={0}>
                <h2
                  className="alerts_title"
                  style={{
                    textAlign: "left",
                    marginBottom: 8,
                    fontSize: isMobile && "18px",
                    lineHeight: isMobile && "24px"
                  }}
                >
                  {getTitle({ isUseBinCaidCodes, isActiveRDR, subStep, t })}
                </h2>
                <Text className="alerts_subtitle" type={"secondary"}>
                  {getDescription({
                    isUseBinCaidCodes,
                    isActiveRDR,
                    subStep,
                    t
                  })}
                </Text>
              </Space>
            </Flex>

            <Flex
              gap={isMobile ? 16 : 32}
              style={{ width: "100%", marginTop: 24 }}
              justify="space-between"
              wrap="wrap"
            >
              <div style={{ flex: "0 1 100%" }}>
                <BillingDescriptorsForm
                  isActiveRDR={isActiveRDR}
                  onSubmit={handleSubmit}
                  form={form}
                  defaultValue={initialValues}
                  isAdmin={isAdmin}
                  errorDescriptors={errorDescriptors}
                  errorBinCodes={errorBinCodes}
                  errorCaidCodes={errorCaidCodes}
                  theme={theme}
                  createBA={isCreatingBa}
                  selectedPayment={selectedPayment}
                  subStep={subStep}
                  estimatedDescriptors={estimatedDescriptors}
                  suggestionsLoading={suggestionsLoading}
                />
              </div>
              {!isMobile && (
                <Flex justify="center" style={{ width: "100%" }}>
                  <ButtonComponent
                    type="primary"
                    size="large"
                    loading={userFetching}
                    onClick={onSubmit}
                    className={"flex center"}
                    style={{ width: "100%", marginTop: "auto" }}
                  >
                    {t("Continue")}
                  </ButtonComponent>
                </Flex>
              )}
              {!isUseBinCaidCodes &&
                (showArticle ? (
                  <BillingDescriptorArticleCard videoTitle={videoTitle} />
                ) : (
                  <Flex
                    gap={20}
                    vertical
                    style={{
                      width: "100%",
                      maxWidth: "100%"
                    }}
                  >
                    <div>
                      <Text style={{ fontSize: isMobile ? 14 : 16 }} strong>
                        {t(videoTitle)}
                      </Text>
                      <div
                        style={{
                          borderRadius: 12,
                          overflow: "hidden",
                          height: isMobile ? 240 : 370,
                          marginTop: 24
                        }}
                      >
                        {videoUrl && !videoFailed ? (
                          <ReactPlayer
                            key={videoUrl}
                            controls={true}
                            width={"100%"}
                            height={"100%"}
                            playing={false}
                            url={videoUrl}
                            onError={() => setVideoFailed(true)}
                          />
                        ) : (
                          <Flex
                            align="center"
                            justify="center"
                            style={{ width: "100%", height: "100%" }}
                          >
                            <Text type="secondary">{t("Video is unavailable.")}</Text>
                          </Flex>
                        )}
                      </div>
                    </div>
                  </Flex>
                ))}
            </Flex>
          </Flex>
        </div>
      </Spin>
      {isMobile && (
        <div className="pp-sticky-cta">
          <MobileCtaButtons
            isMobile={isMobile}
            s
            onSaveForLater={handleNavigate}
            onBack={handleBack}
          />
          <ButtonComponent
            type="primary"
            size="large"
            loading={userFetching}
            onClick={onSubmit}
            className={"flex center"}
            style={{ width: "100%" }}
          >
            {t("Continue")}
          </ButtonComponent>
        </div>
      )}
    </Layout>
  )
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  nav: state.nav,
  userFetching: state.user.fetching,
  businessAccountFetching: state.user.businessAccountFetching,
  businessAccount: state.user.businessAccount,
  businessAccounts: state.user.businessAccounts,
  enrollments: state.user.enrollments,
  billingDescriptors: state.user.billingDescriptors,
  errorDescriptors: state.user.errorDescriptors,
  errorBinCodes: state.user.errorBinCodes,
  errorCaidCodes: state.user.errorCaidCodes,
  paymentProcessors: state.user.paymentProcessors,

  nextCreateAccountStep: state.user.nextCreateAccountStep,
  isManualRefundsConnected: state.user.isManualRefundsConnected
})

// wraps dispatch to create nicer functions to call within our component
const mapDispatchToProps = (dispatch) => ({
  getBillingDescriptors: (params) => dispatch(UserActions.getBillingDescriptorsRequest(params)),
  createBillingDescriptor: (data) => dispatch(UserActions.createBillingDescriptorRequest(data)),
  clearStep: () => dispatch(UserActions.userSetProp("nextManageEnrollmentStep", null))
})

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingAlertsPage)
